/**
 * jobcrawls' floating inspector panel, on the design system. The generic overlay
 * (client/InspectorOverlay.tsx) renders it through its `Panel` prop, and StaticInspectorMount uses
 * the same frame and readout for routes that never hydrate.
 */
import React from 'react';
import { Card } from '../../design-system/ui/Card/Card';
import { Switch } from '../../design-system/ui/Switch/Switch';
import type { InspectorPanelProps } from '../client/InspectorOverlay';
import { INSPECTOR_KEYS_HINT } from '../client/inspector-keys-hint';

/** A Card pinned bottom-right, above everything on the page. */
export function InspectorPanelFrame({ children }: { children: React.ReactNode }): React.ReactElement {
  return (
    <Card
      padding="sm"
      role="region"
      aria-label="Translation inspector"
      data-i18n-inspector-ui=""
      className="flex w-80 max-w-[calc(100vw-2rem)] flex-col gap-3 shadow-jc-lg"
      style={{ position: 'fixed', bottom: 16, right: 16, zIndex: 2147483000 }}
    >
      <span className="type-eyebrow text-jc-ink-secondary">Translation inspector</span>
      {children}
    </Card>
  );
}

export function InspectorKeysHint(): React.ReactElement {
  return <p className="type-caption text-jc-ink-secondary">{INSPECTOR_KEYS_HINT}</p>;
}

/**
 * What the reviewer last copied with the inspector (kept for the browser tab session, so it
 * survives moving to /translation-review). The page can't read the real clipboard without a
 * permission prompt, so anything copied outside the inspector doesn't show here.
 */
export function ClipboardReadout({ text, id }: { text: string | null; id?: string }): React.ReactElement {
  return (
    <div className="flex flex-col gap-1 border-t border-jc-card-border pt-3">
      <span className="type-eyebrow text-jc-ink-secondary">Last copied</span>
      <div
        id={id}
        aria-live="polite"
        data-empty={text ? undefined : ''}
        className="max-h-16 overflow-auto break-all rounded-jc-info bg-jc-elevated px-2 py-1.5 font-mono text-sm text-jc-ink data-empty:font-sans data-empty:text-jc-ink-secondary"
      >
        {text ?? 'Nothing yet'}
      </div>
    </div>
  );
}

export function InspectorPanel({
  enabled,
  setEnabled,
  canShowEnglish,
  showEnglish,
  setShowEnglish,
  lastCopied,
}: InspectorPanelProps): React.ReactElement {
  return (
    <InspectorPanelFrame>
      <Switch label="Highlight strings" checked={enabled} onCheckedChange={setEnabled} className="w-full" />
      {enabled && canShowEnglish && (
        <Switch
          label="Show English"
          description="Or press E"
          checked={showEnglish}
          onCheckedChange={setShowEnglish}
          className="w-full"
        />
      )}
      {enabled && (
        <>
          <InspectorKeysHint />
          <ClipboardReadout text={lastCopied} />
        </>
      )}
    </InspectorPanelFrame>
  );
}
